// @context TGW-2195 入口（entry）画面の状態計算を純関数に切り出す。
//   React Testing Library がリポジトリに無いため、画面操作は E2E、判定ロジックだけを jest で固定する。
//   詳細設計書 2026-09-08-tgw-2195-detailed-design.md §3.5 が正本。

// 入場時にどのページから始めるか。
// フラグ OFF は従来の1本フォーム（0）。ON でも「新規かつタイトルが空」のときだけ入口を出す
// （既存編集・タイトル済みの下書きは基本ページから。要件 F5）。
export const initialPage = ({ entryFlowEnabled, isNew, title }) => {
  if (!entryFlowEnabled) return 0;
  if (isNew && !(title || '').trim()) return 'entry';
  return 'basic';
};

// @context TGW-2195 履歴一覧の「戻る」は開いた画面へ戻し、入口からコピーできた時だけ基本ページへ進める。
export const historyDestination = ({ origin, copied }) =>
  copied && origin === 'entry' ? 'basic' : origin;

// @context TGW-2677 旧1本フォーム（page=0）だけに「過去のイベントのコピーでつくる」を出す。
//   入口ページ（entry.js）に同じ導線があるため、フラグ ON の基本・詳細ページでは出さない。
//   表示条件は cc319e60b7 で削除される前の main.js と同じ（サークルあり・履歴あり・新規作成）。
export const legacyHistoryButtonVisible = ({
  page,
  circleId,
  isShowHistoryButton,
  isNew,
  eventIsNew,
}) =>
  page === 0 && !!circleId && !!isShowHistoryButton && !!isNew && !!eventIsNew;

// @context TGW-2195 手入力中のイベント名だけを補助機能の上書き確認対象にし、空欄や補助機能が入れた候補には確認を重ねない。
export const shouldConfirmOverwrite = ({ title, editedByUser }) =>
  !!editedByUser && !!(title || '').trim();

// 候補の再生成回数の上限（F1-7）。サーバー側の「1イベント4回」（初回1＋再生成3）と対になる。
export const MAX_REGEN = 3;

export const INITIAL_SUGGEST = {
  status: 'idle',
  titles: [],
  shown: [],
  regenCount: 0,
};

export const canRegenerate = (s) =>
  s.status === 'ready' && s.regenCount < MAX_REGEN;

// 'idle' | 'loading' | 'ready' | 'hidden'
// 初回の失敗は枠ごと畳む（hidden）が、再生成の失敗は表示中の候補を残す（F1-13）。
export const nextSuggestionState = (s, a) => {
  switch (a.type) {
    case 'request':
      return { ...s, status: 'loading' };
    case 'success':
      return {
        ...s,
        status: 'ready',
        titles: a.titles,
        shown: [...s.shown, ...a.titles],
        regenCount: a.regen ? s.regenCount + 1 : s.regenCount,
      };
    case 'failure':
      return { ...s, status: s.titles.length ? 'ready' : 'hidden' };
    default:
      return s;
  }
};

// たたき台バナー（F2-2）は AI・ジャンル由来のときだけ。
// 過去コピーは「自分で作った内容」なので出さない。
export const bannerVisible = (draftSource) =>
  draftSource === 'ai' || draftSource === 'genre';

// @context TGW-2195 ジャンル選択だけでイベント名を決められるよう、未入力情報を補わない汎用タイトルを固定する。
//   本文は既存の定型を呼び出し側から受け取り、ジャンル名はカテゴリ付与のヒントとして別に保持する。
export const GENRE_DRAFTS = {
  'スポーツ・運動': '気軽にスポーツ・運動を楽しむ交流会',
  'グルメ・食事会': 'みんなで食事と会話を楽しむ交流会',
  '友達作り・交流会': '気軽に話せる友達作り・交流イベント',
  アウトドア: 'みんなでアウトドアを楽しむ交流会',
  '音楽・ライブ': '音楽・ライブを一緒に楽しむ交流会',
  '勉強会・ビジネス': '一緒に学び交流できる勉強会・交流会',
};

export const genreDraftFor = (label, body) => ({
  title: GENRE_DRAFTS[label],
  body,
  source: 'genre',
  genre: label,
});

// @context TGW-2195 補助の切替時にタイトル・本文・由来・ジャンルを一組で更新し、古い選択を残さない。
export const applyEntryDraftState = (state, draft) => {
  const body = draft.body || '';
  return {
    ...state,
    titleText: (draft.title || '').trim(),
    bodyText: body,
    draftSource: draft.source,
    genre: draft.genre || null,
    aiSuggested: {
      title: draft.source === 'ai',
      body: draft.source === 'ai' && body.length > 0,
    },
    error: '',
    aiError: '',
  };
};

export const changeEntryTitleState = (state, titleText) => ({
  ...state,
  titleText,
  aiSuggested: { ...state.aiSuggested, title: false },
});

// @context TGW-2195 基本ページのAI（一言から作る・写真から読み取る）で反映した項目も「AIの提案」として示す。
//   入口の由来（ジャンル等）のまま残すと、バナーとピルが実際の中身と食い違う（PR #2536 Copilot 指摘）。
export const markAiAcceptedState = (state, field) => ({
  draftSource: 'ai',
  aiSuggested: { ...state.aiSuggested, [field]: true },
});

export const entryOperationLocked = ({ saving, aiGenerating }) =>
  !!saving || !!aiGenerating;

// @context TGW-2195 「次へ」だけを保存と遷移の境界にし、保存失敗時は画面状態を維持する。
export const saveEntryTitle = async ({
  post,
  eventId,
  title,
  genre,
  onSaved,
  onFailed,
}) => {
  const trimmed = (title || '').trim();
  if (!trimmed) return false;

  try {
    await post(
      '/api/event_edit/update_title',
      JSON.stringify({
        event_id: eventId,
        title: trimmed,
        genre: genre || undefined,
      }),
      { timeout: 10000 },
    );
    if (onSaved) onSaved(trimmed);
    return true;
  } catch (error) {
    if (onFailed) onFailed(error);
    return false;
  }
};

// @context TGW-2195 AI生成は保存と分離し、利用上限と再試行可能な失敗を入口側で出し分ける。
export const generateEntryDraft = async ({ post, theme }) => {
  const trimmed = (theme || '').trim();
  if (!trimmed) return { status: 'ignored' };

  try {
    const response = await post(
      '/api/event/generate_outline',
      JSON.stringify({ generate: { theme: trimmed } }),
    );
    const result = response.data.result || {};
    return {
      status: 'success',
      draft: {
        title: result.title || trimmed,
        body: result.body || '',
        source: 'ai',
      },
    };
  } catch (error) {
    if (error.response && error.response.status === 429) {
      return { status: 'rate_limited' };
    }
    return { status: 'failure' };
  }
};
